import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import streamApi from '../../../services/streamApi';
import { authApi } from '../../../services/authApi';
import { useAdmin } from '../AdminContext';

function UsersPage() {
  const {
    users,
    usersError,
    setUsersError,
    setAccessDenied,
    setNotice,
    updateUserBalance,
    removePendingMedia,
  } = useAdmin();
  const { refreshing, loadDashboard } = useOutletContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState('');
  const [userMedia, setUserMedia] = useState([]);
  const [userMediaError, setUserMediaError] = useState('');
  const [userMediaLoading, setUserMediaLoading] = useState(false);
  const [actingMediaId, setActingMediaId] = useState('');
  const [grantTargetUserId, setGrantTargetUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantingUserId, setGrantingUserId] = useState('');

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((item) => {
      const username = String(item.username || '').toLowerCase();
      const email = String(item.email || '').toLowerCase();
      return username.includes(query) || email.includes(query);
    });
  }, [users, searchQuery]);

  const expandedUser = users.find((item) => item.id === expandedUserId) || null;

  async function handleUserClick(nextUserId) {
    if (expandedUserId === nextUserId) {
      setExpandedUserId('');
      setUserMedia([]);
      setUserMediaError('');
      return;
    }

    setExpandedUserId(nextUserId);
    setUserMedia([]);
    setUserMediaError('');
    setUserMediaLoading(true);

    try {
      const items = await streamApi.getAdminUserMedia(nextUserId);
      setUserMedia(Array.isArray(items) ? items : []);
    } catch (loadError) {
      if (streamApi.isAdminAccessError(loadError)) {
        setAccessDenied(true);
      } else {
        setUserMediaError(loadError.message || 'Failed to load user media.');
      }
    } finally {
      setUserMediaLoading(false);
    }
  }

  async function handleModeration(mediaId, action) {
    setActingMediaId(mediaId);
    setNotice('');

    try {
      if (action === 'approve') {
        await streamApi.approveMedia(mediaId);
        setNotice('Media approved successfully.');
      } else {
        await streamApi.rejectMedia(mediaId);
        setNotice('Media rejected successfully.');
      }

      removePendingMedia(mediaId);
      if (expandedUserId) {
        const items = await streamApi.getAdminUserMedia(expandedUserId);
        setUserMedia(Array.isArray(items) ? items : []);
      }
    } catch (actionError) {
      setUserMediaError(actionError.message || `Failed to ${action} media.`);
    } finally {
      setActingMediaId('');
    }
  }

  async function handleGrantSpades(targetUser) {
    const parsedAmount = parseInt(grantAmount, 10);
    if (!parsedAmount || parsedAmount <= 0) return;

    setGrantingUserId(targetUser.id);
    setNotice('');
    setUsersError('');

    try {
      const balance = await authApi.grantAdminSpades(targetUser.id, parsedAmount);
      updateUserBalance(targetUser.id, balance.spade_balance);
      setGrantTargetUserId('');
      setGrantAmount('');
      setNotice(`Granted ${parsedAmount.toLocaleString()} spades to ${targetUser.username}.`);
    } catch (grantError) {
      if (streamApi.isAdminAccessError(grantError)) {
        setAccessDenied(true);
      } else {
        setUsersError(grantError.message || 'Failed to grant spades.');
      }
    } finally {
      setGrantingUserId('');
    }
  }

  return (
    <>
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Users</h1>
          <p className="admin-dashboard__subtitle">
            Look up platform users, grant spades, or review their uploaded media.
          </p>
        </div>
        <button
          type="button"
          className="admin-dashboard__refresh"
          onClick={() => loadDashboard({ silent: true })}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      <div className="admin-users__toolbar">
        <div className="admin-users__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            placeholder="Search by username or email..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <span className="admin-users__count">
          {filteredUsers.length} of {users.length}
        </span>
      </div>

      <div className="admin-dashboard__card">
        {usersError ? (
          <div className="admin-dashboard__empty">{usersError}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="admin-dashboard__empty">
            {users.length === 0 ? 'No users found.' : 'No users match your search.'}
          </div>
        ) : (
          <table className="admin-dashboard__table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Spades</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((item) => (
                <React.Fragment key={item.id}>
                  <tr
                    className={`admin-dashboard__user-row ${expandedUserId === item.id ? 'admin-dashboard__user-row--active' : ''}`}
                    onClick={() => handleUserClick(item.id)}
                  >
                    <td className="admin-dashboard__username-cell">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, marginRight: 6, transition: 'transform 0.2s', transform: expandedUserId === item.id ? 'rotate(90deg)' : 'rotate(0)' }}>
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {item.username || '-'}
                    </td>
                    <td>{item.email || '-'}</td>
                    <td>
                      <span className="admin-dashboard__spades-value">
                        &#9824; {(item.spade_balance || 0).toLocaleString()}
                      </span>
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      {grantTargetUserId === item.id ? (
                        <div className="admin-dashboard__grant-form">
                          <input
                            type="number"
                            className="admin-dashboard__grant-input"
                            placeholder="Amount"
                            value={grantAmount}
                            onChange={(event) => setGrantAmount(event.target.value)}
                            min="1"
                            autoFocus
                          />
                          <button
                            type="button"
                            className="admin-dashboard__grant-confirm"
                            onClick={() => handleGrantSpades(item)}
                            disabled={grantingUserId === item.id}
                          >
                            {grantingUserId === item.id ? '...' : 'Send'}
                          </button>
                          <button
                            type="button"
                            className="admin-dashboard__grant-cancel"
                            onClick={() => {
                              setGrantTargetUserId('');
                              setGrantAmount('');
                            }}
                          >
                            X
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="admin-dashboard__grant-btn"
                          onClick={() => {
                            setGrantTargetUserId(item.id);
                            setGrantAmount('');
                          }}
                        >
                          + Spades
                        </button>
                      )}
                    </td>
                  </tr>

                  {expandedUserId === item.id && (
                    <tr>
                      <td colSpan="4" className="admin-dashboard__expanded-cell">
                        {userMediaError ? (
                          <div className="admin-dashboard__empty">{userMediaError}</div>
                        ) : userMediaLoading ? (
                          <div className="admin-dashboard__loading">Loading media...</div>
                        ) : userMedia.length === 0 ? (
                          <div className="admin-dashboard__empty">No media uploaded by this user.</div>
                        ) : (
                          <div className="admin-dashboard__media-grid">
                            {userMedia.map((mediaItem) => {
                              const previewUrl = streamApi.resolveImageUrl(
                                mediaItem.image_preview_url || mediaItem.image_url || mediaItem.image_key
                              );
                              const isActing = actingMediaId === mediaItem.id;

                              return (
                                <div key={mediaItem.id} className="admin-dashboard__media-card">
                                  <div className="admin-dashboard__media-thumb">
                                    {previewUrl ? (
                                      <img src={previewUrl} alt={mediaItem.title || 'Uploaded media'} />
                                    ) : (
                                      <div className="admin-dashboard__media-no-image">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                          <circle cx="8.5" cy="8.5" r="1.5" />
                                          <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                      </div>
                                    )}
                                    <span className={`admin-dashboard__media-status admin-dashboard__media-status--${mediaItem.approval_status || 'pending'}`}>
                                      {mediaItem.approval_status || 'pending'}
                                    </span>
                                  </div>
                                  <div className="admin-dashboard__media-body">
                                    <p className="admin-dashboard__media-title">{mediaItem.title || 'Untitled media'}</p>
                                    <p className="admin-dashboard__media-uploader">
                                      by {expandedUser?.username || mediaItem.uploader_username || mediaItem.owner_id || 'Unknown'}
                                    </p>
                                    {mediaItem.approval_status === 'pending' && (
                                      <div className="admin-dashboard__media-actions">
                                        <button
                                          type="button"
                                          className="admin-dashboard__action-btn admin-dashboard__action-btn--approve"
                                          onClick={() => handleModeration(mediaItem.id, 'approve')}
                                          disabled={isActing}
                                        >
                                          {isActing ? 'Working...' : 'Approve'}
                                        </button>
                                        <button
                                          type="button"
                                          className="admin-dashboard__action-btn admin-dashboard__action-btn--reject"
                                          onClick={() => handleModeration(mediaItem.id, 'reject')}
                                          disabled={isActing}
                                        >
                                          {isActing ? 'Working...' : 'Reject'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default UsersPage;
